import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { axiosDeleteWithBody, axiosGet, axiosPatch, axiosPost, axiosPut } from 'Redux/axiosHelper'
import {
  AddDropInToFamilyPayload,
  DropInFamilyMember,
  LeagueAttendanceExportResp,
  LeagueCreateDropInAttendeePayload,
  LeagueDetailCheckOutPayload,
  LeagueDirectCheckInPayload,
  LeagueDirectCheckOutPayload,
  LeagueDropInCheckoutPayload,
  LeagueParticipantParams,
  LeagueParticipantsResp,
} from 'types/league/leagueAttendance'

export type {
  AddDropInToFamilyPayload,
  DropInFamilyMember,
  LeagueAttendanceExportResp,
  LeagueParticipantParams,
  LeagueParticipant,
  LeagueParticipantsResp,
} from 'types/league/leagueAttendance'

export interface RollCallParticipant {
  _id: string
  firstName?: string
  lastName?: string
  photoId?: string
}

export interface RollCallHistoryItem {
  _id: string
  name: string
  time: string
  leagueId?: string
  participantIds: string[]
  participants: RollCallParticipant[]
}

const getLeagueParticipants = async (params: LeagueParticipantParams) => {
  const response = await axiosGet('v1/league-check-in/participants', params)
  return response.data
}

export const useGetLeagueParticipants = (params: LeagueParticipantParams) => {
  return useQuery<LeagueParticipantsResp>(
    ['league-participants', params.leagueId, params.date],
    () => getLeagueParticipants(params),
    { enabled: !!params.leagueId },
  )
}

// Flow A: direct check-in (status badge click)
const leagueDirectCheckIn = async (payload: LeagueDirectCheckInPayload) => {
  const response = await axiosPost(payload, 'v1/league-admin-check-in/check-in', '', false)
  return response.data
}

export const useLeagueDirectCheckIn = () => {
  const queryClient = useQueryClient()
  return useMutation(leagueDirectCheckIn, {
    onSuccess: () => queryClient.invalidateQueries(['league-participants']),
  })
}

// Flow A: direct check-out (status badge click)
const leagueDirectCheckOut = async (payload: LeagueDirectCheckOutPayload) => {
  const response = await axiosPatch(payload, 'v1/league-admin-check-in/check-out', '', false)
  return response.data
}

export const useLeagueDirectCheckOut = () => {
  const queryClient = useQueryClient()
  return useMutation(leagueDirectCheckOut, {
    onSuccess: () => queryClient.invalidateQueries(['league-participants']),
  })
}

// Undo direct check-in or check-out (3-second window after action)
const leagueUndoCheckInOut = async (checkInId: string) => {
  const response = await axiosPatch({}, `v1/league-admin-check-in/check-undo/${checkInId}`)
  return response.data
}

export const useLeagueUndoCheckInOut = () => {
  const queryClient = useQueryClient()
  return useMutation(leagueUndoCheckInOut, {
    onSuccess: () => queryClient.invalidateQueries(['league-participants']),
  })
}

// Flow B: approve pending check-in request
const leagueCheckInApproval = async (payload: { checkInId: string }) => {
  const response = await axiosPatch(payload, 'v1/league-check-in/check-in-approval')
  return response.data
}

export const useLeagueCheckInApproval = () => {
  const queryClient = useQueryClient()
  return useMutation(leagueCheckInApproval, {
    onSuccess: () => queryClient.invalidateQueries(['league-participants']),
  })
}

// Flow B: detailed check-out
const leagueDetailCheckOut = async (payload: LeagueDetailCheckOutPayload) => {
  const response = await axiosPatch(payload, 'v1/league-check-in/check-out-request')
  return response.data
}

export const useLeagueDetailCheckOut = () => {
  const queryClient = useQueryClient()
  return useMutation(leagueDetailCheckOut, {
    onSuccess: () => queryClient.invalidateQueries(['league-participants']),
  })
}

// Create league drop-in attendee (substitute)
const createLeagueDropInAttendee = async (payload: LeagueCreateDropInAttendeePayload) => {
  const response = await axiosPost(payload, 'v1/league-drop-in/attendee', '', false)
  return response.data
}

export const useCreateLeagueDropInAttendee = () => {
  const queryClient = useQueryClient()
  return useMutation(createLeagueDropInAttendee, {
    onSuccess: () => queryClient.invalidateQueries(['league-participants']),
  })
}

// Drop-out a league drop-in attendee (admin endpoint)
const leagueDropInCheckout = async (payload: LeagueDropInCheckoutPayload) => {
  const response = await axiosPost(payload, 'v1/dropin/checkout-dropin-without-amount', '', false)
  return response.data
}

export const useLeagueDropInCheckout = () => {
  const queryClient = useQueryClient()
  return useMutation(leagueDropInCheckout, {
    onSuccess: () => queryClient.invalidateQueries(['league-participants']),
  })
}

// Remove participant from league (cancels all their active registrations in this league)
const removeFromLeague = async (payload: {
  leagueId: string
  kidId?: string
  guardianId?: string
  authorizedAdultId?: string
  dropInAttendeeId?: string
}) => {
  const { leagueId, ...body } = payload
  const response = await axiosDeleteWithBody(body, `v1/manage-league/${leagueId}/participant`)
  return response.data
}

export const useRemoveFromLeague = () => {
  const queryClient = useQueryClient()
  return useMutation(removeFromLeague, {
    onSuccess: () => queryClient.invalidateQueries(['league-participants']),
  })
}

// Roll-call history for league (pass familyId to scope to a single player's family)
const getLeagueRollCallHistory = async (params: { leagueId: string; date: string; familyId?: string }) => {
  const response = await axiosGet('v1/roll-call/history', params)
  return response.data
}

export const useGetLeagueRollCallHistory = (params: { leagueId: string; date: string; familyId?: string }) => {
  return useQuery<RollCallHistoryItem[]>(
    ['league-roll-call-history', params.leagueId, params.date, params.familyId],
    () => getLeagueRollCallHistory(params),
    { enabled: !!params.leagueId && !!params.date },
  )
}

// History for detail modal
const getLeagueParticipantHistory = async (
  memberId: string,
  params: { leagueId: string; date: string },
) => {
  const response = await axiosGet(`v1/check/recent-check-in/member/${memberId}`, params)
  return response.data
}

export const useGetLeagueParticipantHistory = (
  memberId: string,
  params: { leagueId: string; date: string },
) => {
  return useQuery(
    ['league-participant-history', memberId, params.leagueId, params.date],
    () => getLeagueParticipantHistory(memberId, params),
    { enabled: false },
  )
}

const getLeagueAttendeesExport = async (leagueId: string) => {
  const response = await axiosGet(`v1/manage-league/${leagueId}/export-data`)
  return response.data
}

const getDropInHistory = async (params: { memberId: string; leagueId: string; date: string }) => {
  const response = await axiosGet('v1/dropin/recent-check-in', params)
  return response.data
}

export const useGetDropInHistory = (memberId: string, leagueId: string, date: string) => {
  return useQuery(
    ['dropin-history', memberId, leagueId, date],
    () => getDropInHistory({ memberId, leagueId, date }),
    { enabled: !!(memberId && leagueId && date) },
  )
}

export interface LeagueAdminDropInPayload {
  leagueId: string
  dropInAttendeeId: string
  familyId?: string
}

const leagueAdminDropIn = async (payload: LeagueAdminDropInPayload) => {
  const response = await axiosPost(payload, 'v1/league-admin-check-in/drop-in', '', false)
  return response.data
}

export const useLeagueAdminDropIn = () => {
  const queryClient = useQueryClient()
  return useMutation(leagueAdminDropIn, {
    onSuccess: () => queryClient.invalidateQueries(['league-participants']),
  })
}

export const useGetLeagueAttendeesExport = (leagueId: string) => {
  return useQuery<LeagueAttendanceExportResp[]>(
    ['league-attendees-export', leagueId],
    () => getLeagueAttendeesExport(leagueId),
    { enabled: false },
  )
}

export const getDropInFamilyMembers = async (params: { email: string; leagueId: string; today: string }): Promise<DropInFamilyMember[]> => {
  const response = await axiosGet('v1/dropin/members-by-email', params)
  return response.data
}

const addDropInToFamily = async (payload: AddDropInToFamilyPayload) => {
  const response = await axiosPut(payload, 'v1/dropin/add-to-family')
  return response.data
}

export const useAddDropInToFamily = () => {
  const queryClient = useQueryClient()
  return useMutation(addDropInToFamily, {
    onSuccess: () => queryClient.invalidateQueries(['league-participants']),
  })
}
